// AdminMediaLibrary.js — Fase 15.3: Biblioteca de Medios
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  ADMIN_COLORS, AdminContainer, AdminTopBar, AdminTitle, AdminButton,
  AdminSmallButton, AdminCard, AdminModalOverlay, AdminModalContent,
  AdminModalTitle, AdminModalActions, AdminFormGroup, AdminLabel,
  AdminInput, AdminTextarea, AdminSelect, AdminBadge, AdminLoading,
  AdminEmptyState, AdminPagination, AdminSearchInput, AdminInfoText
} from './AdminShared';
import { mediaLibraryService, mediaTagService } from '../../services/api';
import { useProject } from '../../contexts/ProjectContext';

// ============================================================
// STYLED COMPONENTS
// ============================================================

const FilterBar = styled.div`
  display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; margin-bottom: 1.25rem;
`;

const FilterChip = styled.button`
  padding: 0.3rem 0.75rem; border-radius: 20px; font-size: 0.8rem; font-weight: 500;
  cursor: pointer; transition: all 0.2s; border: 1.5px solid;
  background: ${p => p.$active ? ADMIN_COLORS.accent : ADMIN_COLORS.white};
  color: ${p => p.$active ? '#fff' : ADMIN_COLORS.textLight};
  border-color: ${p => p.$active ? ADMIN_COLORS.accent : ADMIN_COLORS.border};
  &:hover { border-color: ${ADMIN_COLORS.accent}; color: ${p => p.$active ? '#fff' : ADMIN_COLORS.accent}; }
`;

const MediaGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem; margin-bottom: 1rem;
`;

const MediaCard = styled.div`
  background: ${ADMIN_COLORS.white}; border-radius: 12px; overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07); cursor: pointer; transition: all 0.2s;
  border: 2px solid ${p => p.$selected ? ADMIN_COLORS.accent : 'transparent'};
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.12); }
`;

const MediaThumb = styled.div`
  height: 130px; background: ${ADMIN_COLORS.bg}; display: flex;
  align-items: center; justify-content: center; position: relative; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const MediaTypeIcon = styled.div`
  font-size: 2.5rem; color: ${ADMIN_COLORS.textMuted};
`;

const MediaDuration = styled.span`
  position: absolute; bottom: 6px; right: 6px; background: rgba(0,0,0,0.75);
  color: #fff; padding: 2px 6px; border-radius: 4px; font-size: 0.7rem;
`;

const MediaInfo = styled.div`
  padding: 0.75rem;
`;

const MediaCardTitle = styled.div`
  font-weight: 600; font-size: 0.85rem; color: ${ADMIN_COLORS.text};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const MediaMeta = styled.div`
  font-size: 0.75rem; color: ${ADMIN_COLORS.textLight}; margin-top: 0.25rem;
  display: flex; align-items: center; gap: 0.5rem;
`;

const TagPill = styled.span`
  display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 0.7rem;
  font-weight: 500; margin: 2px 2px 0 0;
  background: ${p => p.$color || '#e5e7eb'}20; color: ${p => p.$color || '#6b7280'};
  border: 1px solid ${p => p.$color || '#e5e7eb'};
`;

const TagsRow = styled.div`
  display: flex; flex-wrap: wrap; margin-top: 0.35rem;
`;

const DropZone = styled.div`
  border: 2px dashed ${p => p.$active ? ADMIN_COLORS.accent : ADMIN_COLORS.border};
  border-radius: 12px; padding: 2rem; text-align: center; cursor: pointer;
  transition: all 0.2s; background: ${p => p.$active ? '#f0fdfa' : ADMIN_COLORS.bg};
  &:hover { border-color: ${ADMIN_COLORS.accent}; background: #f0fdfa; }
`;

const TranscriptionBox = styled.div`
  background: ${ADMIN_COLORS.bg}; border-radius: 8px; padding: 1rem;
  max-height: 200px; overflow-y: auto; font-size: 0.85rem; color: ${ADMIN_COLORS.text};
  border: 1px solid ${ADMIN_COLORS.border}; white-space: pre-wrap; line-height: 1.5;
`;

const TagManager = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.35rem; align-items: center;
`;

// ============================================================
// ICONS
// ============================================================

const TYPE_ICONS = {
  video: '🎬', pdf: '📑', imagen: '🖼️', audio: '🎵', presentacion: '📊', otro: '📄'
};

const TYPE_LABELS = {
  video: 'Video', pdf: 'PDF', imagen: 'Imagen', audio: 'Audio', presentacion: 'Presentación', otro: 'Otro'
};

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes > 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
  return (bytes / 1024).toFixed(0) + ' KB';
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const AdminMediaLibrary = () => {
  const { proyecto } = useProject();
  const proyectoId = proyecto?.id;

  // State
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroTag, setFiltroTag] = useState(null);
  const [tags, setTags] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showTagManager, setShowTagManager] = useState(false);
  const searchTimeout = useRef(null);

  // Load media
  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (proyectoId) params.proyecto_id = proyectoId;
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroTag) params.tag_id = filtroTag;
      if (busqueda.trim()) params.busqueda = busqueda.trim();

      const res = await mediaLibraryService.list(params);
      const d = res.data;
      setItems(d.items || []);
      setTotal(d.total || 0);
      setTotalPages(d.total_pages || 1);
    } catch (e) {
      console.error('Error cargando media:', e);
    }
    setLoading(false);
  }, [page, filtroTipo, filtroTag, busqueda, proyectoId]);

  const loadTags = useCallback(async () => {
    try {
      const res = await mediaTagService.list(proyectoId);
      setTags(res.data.tags || []);
    } catch (e) { console.error('Error cargando tags:', e); }
  }, [proyectoId]);

  useEffect(() => { loadMedia(); }, [loadMedia]);
  useEffect(() => { loadTags(); }, [loadTags]);

  const handleSearch = (val) => {
    setBusqueda(val);
    setPage(1);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => loadMedia(), 400);
  };

  const handleFilterTipo = (tipo) => {
    setFiltroTipo(prev => prev === tipo ? '' : tipo);
    setPage(1);
  };

  const handleFilterTag = (tagId) => {
    setFiltroTag(prev => prev === tagId ? null : tagId);
    setPage(1);
  };

  const handleUploadDone = () => {
    setShowUpload(false);
    loadMedia();
    loadTags();
  };

  const handleDetailDone = () => {
    setShowDetail(null);
    loadMedia();
    loadTags();
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <AdminContainer>
      <AdminTopBar>
        <AdminTitle>Biblioteca de Medios</AdminTitle>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <AdminButton $ghost onClick={() => setShowTagManager(true)}>Gestionar Tags</AdminButton>
          <AdminButton onClick={() => setShowUpload(true)}>+ Subir Contenido</AdminButton>
        </div>
      </AdminTopBar>

      {/* Search + Filters */}
      <FilterBar>
        <AdminSearchInput
          placeholder="Buscar por titulo, descripcion..."
          value={busqueda}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <FilterChip $active={!filtroTipo} onClick={() => { setFiltroTipo(''); setPage(1); }}>Todos</FilterChip>
        {Object.keys(TYPE_ICONS).map(tipo => (
          <FilterChip key={tipo} $active={filtroTipo === tipo} onClick={() => handleFilterTipo(tipo)}>
            {TYPE_ICONS[tipo]} {TYPE_LABELS[tipo]}
          </FilterChip>
        ))}
      </FilterBar>

      {/* Tags filter */}
      {tags.length > 0 && (
        <FilterBar>
          <AdminInfoText style={{ marginRight: 4 }}>Tags:</AdminInfoText>
          {tags.map(t => (
            <FilterChip key={t.id} $active={filtroTag === t.id} onClick={() => handleFilterTag(t.id)}
              style={filtroTag === t.id ? {} : { borderColor: t.color, color: t.color }}>
              {t.nombre} ({t.media_count})
            </FilterChip>
          ))}
          {filtroTag && <FilterChip onClick={() => { setFiltroTag(null); setPage(1); }}>Limpiar</FilterChip>}
        </FilterBar>
      )}

      <AdminInfoText style={{ marginBottom: '1rem' }}>
        {total} {total === 1 ? 'elemento' : 'elementos'} en la biblioteca
      </AdminInfoText>

      {/* Grid */}
      {loading ? (
        <AdminLoading>Cargando biblioteca...</AdminLoading>
      ) : items.length === 0 ? (
        <AdminEmptyState>
          {busqueda || filtroTipo || filtroTag
            ? 'No se encontraron resultados con esos filtros'
            : 'La biblioteca esta vacia. Sube contenido para empezar.'}
        </AdminEmptyState>
      ) : (
        <MediaGrid>
          {items.map(item => (
            <MediaCard key={item.id} onClick={() => setShowDetail(item)}>
              <MediaThumb>
                {item.thumbnail_path ? (
                  <img src={item.thumbnail_path} alt={item.titulo} />
                ) : (
                  <MediaTypeIcon>{TYPE_ICONS[item.tipo] || '📄'}</MediaTypeIcon>
                )}
                {item.tipo === 'video' && item.duracion_segundos > 0 && (
                  <MediaDuration>{formatDuration(item.duracion_segundos)}</MediaDuration>
                )}
              </MediaThumb>
              <MediaInfo>
                <MediaCardTitle title={item.titulo}>{item.titulo}</MediaCardTitle>
                <MediaMeta>
                  <AdminBadge $bg={item.tipo === 'video' ? '#dbeafe' : item.tipo === 'pdf' ? '#fef3c7' : '#f3e8ff'}
                    $color={item.tipo === 'video' ? '#1d4ed8' : item.tipo === 'pdf' ? '#92400e' : '#7c3aed'}>
                    {TYPE_ICONS[item.tipo]} {TYPE_LABELS[item.tipo]}
                  </AdminBadge>
                  {item.file_size_bytes > 0 && <span>{formatFileSize(item.file_size_bytes)}</span>}
                  {item.vimeo_id && <span>Vimeo</span>}
                </MediaMeta>
                {item.tags_array && item.tags_array.length > 0 && (
                  <TagsRow>
                    {item.tags_array.slice(0, 3).map(t => (
                      <TagPill key={t.id} $color={t.color}>{t.nombre}</TagPill>
                    ))}
                    {item.tags_array.length > 3 && <TagPill>+{item.tags_array.length - 3}</TagPill>}
                  </TagsRow>
                )}
              </MediaInfo>
            </MediaCard>
          ))}
        </MediaGrid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <AdminPagination>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</button>
          <span>Pagina {page} de {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Siguiente</button>
        </AdminPagination>
      )}

      {/* Modals */}
      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={handleUploadDone} tags={tags} proyectoId={proyectoId} />}
      {showDetail && <DetailModal media={showDetail} onClose={handleDetailDone} tags={tags} onRefreshTags={loadTags} />}
      {showTagManager && <TagManagerModal tags={tags} onClose={() => { setShowTagManager(false); loadTags(); }} proyectoId={proyectoId} />}
    </AdminContainer>
  );
};

// ============================================================
// UPLOAD MODAL
// ============================================================

const UploadModal = ({ onClose, onDone, tags, proyectoId }) => {
  const [modo, setModo] = useState('archivo'); // archivo | vimeo
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [vimeoUrl, setVimeoUrl] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); if (!titulo) setTitulo(f.name.replace(/\.[^.]+$/, '')); }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); if (!titulo) setTitulo(f.name.replace(/\.[^.]+$/, '')); }
  };

  const toggleTag = (id) => {
    setSelectedTags(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };

  const handleSubmit = async () => {
    if (!titulo.trim()) return alert('El titulo es requerido');
    if (modo === 'archivo' && !file) return alert('Selecciona un archivo');
    if (modo === 'vimeo' && !vimeoUrl.trim()) return alert('Ingresa la URL de Vimeo');

    setSaving(true);
    try {
      if (modo === 'vimeo') {
        const fd = new FormData();
        fd.append('titulo', titulo.trim());
        fd.append('descripcion', descripcion.trim());
        fd.append('vimeo_url', vimeoUrl.trim());
        fd.append('tag_ids', JSON.stringify(selectedTags));
        if (proyectoId) fd.append('proyecto_id', proyectoId);
        await mediaLibraryService.upload(fd);
      } else {
        const fd = new FormData();
        fd.append('archivo', file);
        fd.append('titulo', titulo.trim());
        fd.append('descripcion', descripcion.trim());
        fd.append('tag_ids', JSON.stringify(selectedTags));
        if (proyectoId) fd.append('proyecto_id', proyectoId);
        await mediaLibraryService.upload(fd);
      }
      onDone();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  return (
    <AdminModalOverlay onClick={onClose}>
      <AdminModalContent $wide onClick={e => e.stopPropagation()}>
        <AdminModalTitle>Subir Contenido a la Biblioteca</AdminModalTitle>

        {/* Modo selector */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
          <FilterChip $active={modo === 'archivo'} onClick={() => setModo('archivo')}>📁 Archivo Local</FilterChip>
          <FilterChip $active={modo === 'vimeo'} onClick={() => setModo('vimeo')}>🎬 Video de Vimeo</FilterChip>
        </div>

        {modo === 'archivo' ? (
          <DropZone
            $active={dragActive}
            onDragOver={e => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect}
              accept="video/*,image/*,audio/*,.pdf,.ppt,.pptx" />
            {file ? (
              <div>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>{TYPE_ICONS[file.type?.split('/')[0]] || '📄'}</div>
                <strong>{file.name}</strong>
                <div style={{ color: ADMIN_COLORS.textLight, fontSize: '0.85rem' }}>{formatFileSize(file.size)}</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>📂</div>
                <strong>Arrastra un archivo aqui</strong>
                <div style={{ color: ADMIN_COLORS.textLight, fontSize: '0.85rem', marginTop: 4 }}>
                  o haz clic para seleccionar (video, PDF, imagen, audio — max 50MB)
                </div>
              </div>
            )}
          </DropZone>
        ) : (
          <AdminFormGroup>
            <AdminLabel>URL de Vimeo</AdminLabel>
            <AdminInput placeholder="https://vimeo.com/123456789 o https://player.vimeo.com/video/123456789?h=abc"
              value={vimeoUrl} onChange={e => setVimeoUrl(e.target.value)} />
            <AdminInfoText style={{ marginTop: 4 }}>Se importara automaticamente la duracion y thumbnail desde Vimeo</AdminInfoText>
          </AdminFormGroup>
        )}

        <div style={{ marginTop: '1rem' }}>
          <AdminFormGroup>
            <AdminLabel>Titulo *</AdminLabel>
            <AdminInput value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Titulo del contenido" />
          </AdminFormGroup>

          <AdminFormGroup>
            <AdminLabel>Descripcion</AdminLabel>
            <AdminTextarea value={descripcion} onChange={e => setDescripcion(e.target.value)}
              placeholder="Descripcion opcional" rows={2} />
          </AdminFormGroup>

          {tags.length > 0 && (
            <AdminFormGroup>
              <AdminLabel>Tags</AdminLabel>
              <TagManager>
                {tags.map(t => (
                  <FilterChip key={t.id} $active={selectedTags.includes(t.id)} onClick={() => toggleTag(t.id)}
                    style={selectedTags.includes(t.id) ? {} : { borderColor: t.color, color: t.color }}>
                    {t.nombre}
                  </FilterChip>
                ))}
              </TagManager>
            </AdminFormGroup>
          )}
        </div>

        <AdminModalActions>
          <AdminButton $ghost onClick={onClose}>Cancelar</AdminButton>
          <AdminButton onClick={handleSubmit} disabled={saving}>
            {saving ? 'Subiendo...' : 'Subir a Biblioteca'}
          </AdminButton>
        </AdminModalActions>
      </AdminModalContent>
    </AdminModalOverlay>
  );
};

// ============================================================
// DETAIL / EDIT MODAL
// ============================================================

const DetailModal = ({ media, onClose, tags, onRefreshTags }) => {
  const [editing, setEditing] = useState(false);
  const [titulo, setTitulo] = useState(media.titulo || '');
  const [descripcion, setDescripcion] = useState(media.descripcion || '');
  const [transcripcion, setTranscripcion] = useState(media.transcripcion || '');
  const [editTranscripcion, setEditTranscripcion] = useState(false);
  const [selectedTags, setSelectedTags] = useState((media.tags_array || []).map(t => t.id));
  const [saving, setSaving] = useState(false);
  const [importingTranscript, setImportingTranscript] = useState(false);

  const toggleTag = (id) => {
    setSelectedTags(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await mediaLibraryService.update(media.id, {
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        transcripcion: editTranscripcion ? transcripcion : undefined,
        tag_ids: selectedTags,
      });
      onClose();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  const handleImportTranscript = async () => {
    setImportingTranscript(true);
    try {
      const res = await mediaLibraryService.importVimeoTranscript(media.id);
      const d = res.data;
      if (d.success) {
        alert(`Transcripcion importada (${d.caracteres} caracteres, idioma: ${d.idioma}, formato: ${d.formato})`);
        // Reload transcription
        const mediaRes = await mediaLibraryService.getById(media.id);
        setTranscripcion(mediaRes.data.media.transcripcion || '');
      }
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
    setImportingTranscript(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Eliminar este contenido de la biblioteca?')) return;
    try {
      await mediaLibraryService.delete(media.id);
      onClose();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  return (
    <AdminModalOverlay onClick={onClose}>
      <AdminModalContent $extraWide onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <AdminModalTitle style={{ margin: 0 }}>
            {TYPE_ICONS[media.tipo]} {editing ? 'Editar Media' : 'Detalle de Media'}
          </AdminModalTitle>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {!editing && <AdminSmallButton $ghost onClick={() => setEditing(true)}>Editar</AdminSmallButton>}
            <AdminSmallButton $danger onClick={handleDelete}>Eliminar</AdminSmallButton>
          </div>
        </div>

        <div style={{ marginTop: '1.25rem', display: 'grid', gridTemplateColumns: media.tipo === 'video' ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>
          {/* Left: Preview */}
          <div>
            {media.tipo === 'video' && media.vimeo_id ? (
              <div style={{ position: 'relative', paddingBottom: '56.25%', borderRadius: 8, overflow: 'hidden', background: '#000' }}>
                <iframe
                  src={`https://player.vimeo.com/video/${media.vimeo_id}${media.hash_privacidad ? '?h=' + media.hash_privacidad : ''}`}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                  allow="autoplay; fullscreen" allowFullScreen title={media.titulo}
                />
              </div>
            ) : media.tipo === 'imagen' && media.file_path ? (
              <img src={`/backend${media.file_path}`} alt={media.titulo}
                style={{ width: '100%', borderRadius: 8, maxHeight: 300, objectFit: 'contain' }} />
            ) : (
              <div style={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: ADMIN_COLORS.bg, borderRadius: 8, fontSize: '4rem' }}>
                {TYPE_ICONS[media.tipo]}
              </div>
            )}

            {/* Meta info */}
            <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: ADMIN_COLORS.textLight }}>
              {media.tipo === 'video' && media.duracion_segundos > 0 && <div>Duracion: {formatDuration(media.duracion_segundos)}</div>}
              {media.vimeo_id && <div>Vimeo ID: {media.vimeo_id}</div>}
              {media.file_size_bytes > 0 && <div>Tamano: {formatFileSize(media.file_size_bytes)}</div>}
              {media.original_name && <div>Archivo: {media.original_name}</div>}
              <div>Creado: {new Date(media.created_at).toLocaleDateString('es')}</div>
            </div>
          </div>

          {/* Right: Fields */}
          <div>
            <AdminFormGroup>
              <AdminLabel>Titulo</AdminLabel>
              {editing ? (
                <AdminInput value={titulo} onChange={e => setTitulo(e.target.value)} />
              ) : (
                <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>{media.titulo}</div>
              )}
            </AdminFormGroup>

            <AdminFormGroup>
              <AdminLabel>Descripcion</AdminLabel>
              {editing ? (
                <AdminTextarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={2} />
              ) : (
                <div style={{ fontSize: '0.9rem', color: media.descripcion ? ADMIN_COLORS.text : ADMIN_COLORS.textMuted }}>
                  {media.descripcion || 'Sin descripcion'}
                </div>
              )}
            </AdminFormGroup>

            <AdminFormGroup>
              <AdminLabel>Tags</AdminLabel>
              {editing ? (
                <TagManager>
                  {tags.map(t => (
                    <FilterChip key={t.id} $active={selectedTags.includes(t.id)} onClick={() => toggleTag(t.id)}
                      style={selectedTags.includes(t.id) ? {} : { borderColor: t.color, color: t.color }}>
                      {t.nombre}
                    </FilterChip>
                  ))}
                </TagManager>
              ) : (
                <TagsRow>
                  {(media.tags_array || []).length > 0
                    ? media.tags_array.map(t => <TagPill key={t.id} $color={t.color}>{t.nombre}</TagPill>)
                    : <AdminInfoText>Sin tags</AdminInfoText>}
                </TagsRow>
              )}
            </AdminFormGroup>

            {/* Transcription */}
            <AdminFormGroup>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <AdminLabel>Transcripcion</AdminLabel>
                <div style={{ display: 'flex', gap: '0.35rem' }}>
                  {media.tipo === 'video' && media.vimeo_id && (
                    <AdminSmallButton $ghost onClick={handleImportTranscript} disabled={importingTranscript}>
                      {importingTranscript ? 'Importando...' : 'Importar de Vimeo'}
                    </AdminSmallButton>
                  )}
                  {editing && (
                    <AdminSmallButton $ghost onClick={() => setEditTranscripcion(!editTranscripcion)}>
                      {editTranscripcion ? 'Cancelar edicion' : 'Editar'}
                    </AdminSmallButton>
                  )}
                </div>
              </div>
              {editing && editTranscripcion ? (
                <AdminTextarea value={transcripcion} onChange={e => setTranscripcion(e.target.value)}
                  rows={8} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
              ) : transcripcion ? (
                <TranscriptionBox>{transcripcion.substring(0, 2000)}{transcripcion.length > 2000 ? '...' : ''}</TranscriptionBox>
              ) : (
                <AdminInfoText>Sin transcripcion</AdminInfoText>
              )}
            </AdminFormGroup>
          </div>
        </div>

        <AdminModalActions>
          {editing ? (
            <>
              <AdminButton $ghost onClick={() => setEditing(false)}>Cancelar</AdminButton>
              <AdminButton onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar Cambios'}</AdminButton>
            </>
          ) : (
            <AdminButton $ghost onClick={onClose}>Cerrar</AdminButton>
          )}
        </AdminModalActions>
      </AdminModalContent>
    </AdminModalOverlay>
  );
};

// ============================================================
// TAG MANAGER MODAL
// ============================================================

const TagManagerModal = ({ tags, onClose, proyectoId }) => {
  const [localTags, setLocalTags] = useState(tags);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#6B7280');

  const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280', '#14b6cb'];

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await mediaTagService.create({ nombre: newName.trim(), color: newColor, proyecto_id: proyectoId });
      setLocalTags([...localTags, res.data.tag]);
      setNewName('');
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este tag?')) return;
    try {
      await mediaTagService.delete(id);
      setLocalTags(localTags.filter(t => t.id !== id));
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  return (
    <AdminModalOverlay onClick={onClose}>
      <AdminModalContent onClick={e => e.stopPropagation()}>
        <AdminModalTitle>Gestionar Tags</AdminModalTitle>

        {/* Create */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', alignItems: 'flex-end' }}>
          <AdminFormGroup style={{ flex: 1, marginBottom: 0 }}>
            <AdminLabel>Nuevo tag</AdminLabel>
            <AdminInput value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nombre del tag"
              onKeyDown={e => e.key === 'Enter' && handleCreate()} />
          </AdminFormGroup>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', paddingBottom: 2 }}>
            {COLORS.map(c => (
              <div key={c} onClick={() => setNewColor(c)}
                style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer',
                  border: newColor === c ? '3px solid #1e293b' : '2px solid transparent' }} />
            ))}
          </div>
          <AdminButton onClick={handleCreate} style={{ height: 38 }}>+</AdminButton>
        </div>

        {/* List */}
        {localTags.length === 0 ? (
          <AdminEmptyState>No hay tags. Crea el primero.</AdminEmptyState>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {localTags.map(t => (
              <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0.5rem 0.75rem', background: ADMIN_COLORS.bg, borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: t.color }} />
                  <span style={{ fontWeight: 500 }}>{t.nombre}</span>
                  <span style={{ fontSize: '0.8rem', color: ADMIN_COLORS.textLight }}>({t.media_count || 0} media)</span>
                </div>
                <AdminSmallButton $danger onClick={() => handleDelete(t.id)}>Eliminar</AdminSmallButton>
              </div>
            ))}
          </div>
        )}

        <AdminModalActions>
          <AdminButton $ghost onClick={onClose}>Cerrar</AdminButton>
        </AdminModalActions>
      </AdminModalContent>
    </AdminModalOverlay>
  );
};

export default AdminMediaLibrary;
